import React from 'react';
import { Box, Text, VStack } from '@chakra-ui/react';
import { motion, Transition } from 'framer-motion';
import { heroStatItemVariants, HERO_STAT_BADGE_STYLES } from './heroConstants';

const MotionBox = motion.create(Box);

// Pulsing animation for highlighted CTAs (e.g., "Rate Now")
const pulseAnimation = {
  scale: [1, 1.02, 1],
  boxShadow: [
    '0 0 0 0 rgba(139, 92, 246, 0.4)',
    '0 0 0 6px rgba(139, 92, 246, 0)',
    '0 0 0 0 rgba(139, 92, 246, 0)',
  ],
};

const pulseTransition: Transition = {
  duration: 2,
  repeat: Infinity,
  ease: 'easeInOut',
};

export interface HeroStatBadgeProps {
  /** Icon element to display */
  icon: React.ReactNode;
  /** Primary value (text, number, or custom element) */
  value: string | number | React.ReactNode;
  /** Label text below the value */
  label: string;
  /** Click handler - makes the badge interactive */
  onClick?: () => void;
  /** Enable pulsing animation for CTAs */
  highlighted?: boolean;
  /** Custom border color (e.g., for compatibility indicators) */
  borderColor?: string;
}

/**
 * Glassmorphic stat badge for hero sections.
 * Displays an icon, value, and label with optional interactivity.
 */
export function HeroStatBadge({
  icon,
  value,
  label,
  onClick,
  highlighted = false,
  borderColor,
}: HeroStatBadgeProps) {
  return (
    <MotionBox
      variants={heroStatItemVariants}
      bg={HERO_STAT_BADGE_STYLES.bg}
      backdropFilter={HERO_STAT_BADGE_STYLES.backdropFilter}
      borderRadius={HERO_STAT_BADGE_STYLES.borderRadius}
      px={3}
      py={2}
      display="flex"
      alignItems="center"
      gap={2}
      border="1px solid"
      borderColor={borderColor || 'var(--card-divider)'}
      cursor={onClick ? 'pointer' : 'default'}
      onClick={onClick}
      _hover={onClick ? { bg: HERO_STAT_BADGE_STYLES.hoverBg } : undefined}
      style={{ transition: 'background 0.2s ease' }}
      animate={highlighted ? pulseAnimation : undefined}
      transition={highlighted ? pulseTransition : undefined}
    >
      <Box color="var(--card-text)" opacity={0.9} flexShrink={0}>
        {icon}
      </Box>
      <VStack gap={0} align="start">
        <Text fontSize="md" fontWeight="bold" color="var(--card-text)" lineHeight={1}>
          {value}
        </Text>
        <Text fontSize="xs" color="var(--card-text-2)" lineHeight={1} mt={0.5}>
          {label}
        </Text>
      </VStack>
    </MotionBox>
  );
}

export default HeroStatBadge;
